// ─────────────────────────────────────────────────────────────────────
// PLATFORM ENGINE (Padel24Engine) — the live runtime engine object.
//
// This is the central wiring object: it composes the pure engine modules
// (elo, booking, km, seriespill, banepad, americano, padel24, singleMatch,
// constants, playerDb, …) into the namespaced `PlatformEngine`, aliases it as
// `Padel24Engine` (the name every view uses), and attaches the 11 tournament-
// format engines (americano/mexicano/mixicano/team_mexicano/mix_americano/
// club_*/klubbmesterskap/seriespill/booking). Moved verbatim out of the
// monolith (legacy/App.jsx) in PR-AO; the wiring is unchanged. `PlatformEngine`
// is typed `any` because the format engines are attached dynamically after the
// object literal (Padel24Engine.americano = …), matching the original.
//
// `banepadTournamentView` (tournament-type → app-view name) travels with the
// block; it is a small pure mapper threaded into the banepad screens.
// ─────────────────────────────────────────────────────────────────────
import { toMinHHMM, fromMinHHMM } from '../lib/date';
import {
  AMERICANO_DEFAULT_CONFIG,
  CLUB_AMERICANO_DEFAULT_CONFIG,
  CLUB_MEXICANO_DEFAULT_CONFIG,
  CLUB_TEAM_AMERICANO_DEFAULT_CONFIG,
  MEXICANO_DEFAULT_CONFIG,
  MEXICANO_POOL_SIZES,
  MIXICANO_DEFAULT_CONFIG,
  MIX_AMERICANO_DEFAULT_CONFIG,
  TEAM_MEXICANO_DEFAULT_CONFIG,
  buildAmericanoHistory,
  calcAmericanoStandings,
  calcClubStandings,
  calcTeamMexicanoStandings,
  createAmericanoTournament,
  createClubAmericanoTournament,
  createClubMexicanoTournament,
  createClubTeamAmericanoTournament,
  createMexicanoTournament,
  createMixAmericanoTournament,
  createMixicanoTournament,
  createTeamMexicanoTournament,
  defaultMexicanoPoolSize,
  effectiveGender,
  fillPlaceholderInAmericano,
  generateAmericanoFinals,
  generateAmericanoRound,
  generateClubAmericanoFinals,
  generateClubAmericanoRound,
  generateClubMexicanoFinals,
  generateClubMexicanoRound,
  generateClubTeamAmericanoFinals,
  generateClubTeamAmericanoRound,
  generateMexicanoRound,
  generateMixAmericanoRound,
  generateMixicanoRound,
  generateTeamMexicanoRound,
  replacePlayerInAmericano,
  validMexicanoPoolSizes,
} from './americano';
import {
  BOOKING_DEFAULT_BUFFER_MIN,
  BOOKING_DEFAULT_CONFIG,
  BOOKING_DEFAULT_DURATION_MIN,
  DAY_OF_WEEK_KEYS,
  DAY_OF_WEEK_LABELS,
  HOLIDAYS,
  calcBookingPrice,
  calcCourtBookingPrice,
  courtDisplayName,
  courtFacilities,
  courtPricesSet,
  createBooking,
  dayOfWeekKey,
  expandRecurrence,
  findFreeSlots,
  findZoneAt,
  getBookingsForDay,
  getHolidaySet,
  holidayName,
  isHoliday,
  isNorwegianHoliday,
  validateBooking,
  zoneMatches,
} from './booking';
import { EngineSettings, TOURNAMENT_TYPES, getTournamentType } from './constants';
import {
  applyEloUpdate,
  calcEloChange,
  calcMovMultiplier,
  formatBoost,
  getPlayerKMultiplier,
  getPlayerStatus,
} from './elo';
import { ELO_LEVELS, getEloLevel } from './eloLevels';
import { ENGINE_CHANGELOG } from './engineChangelog';
import {
  KM_CLASS_TEMPLATES,
  KM_DEFAULT_BUFFER_MINUTES,
  KM_DEFAULT_GROUP_MATCH_FORMAT,
  KM_DEFAULT_GROUP_MINUTES,
  KM_DEFAULT_KNOCKOUT_MINUTES,
  KM_MATCH_FORMATS,
  applyCourtPriority,
  buildBracketWithLuckyLosers,
  buildCrossOverBracket,
  buildPairsFromPlayers,
  calcGroupStandings,
  calcKlubbmesterskapBudget,
  calculateKMMatchCount,
  calculateKMTimeBudget,
  createKlubbmesterskapTournament,
  determineBracketStructure,
  determineGroupSizes,
  fipBracketSeed,
  roundRobinSchedule,
  scheduleKMMatches,
  snakeSeedGroups,
  sortPairsBySeed,
} from './km';
import { ENGINE_VERSION, ENGINE_VERSION_DATE, ENGINE_VERSION_NAME } from './meta';
import {
  applyBlockPromotionDemotion,
  applyMatchResult,
  applyPromotionDemotion,
  awardPhaseBonuses,
  buildFullSchedule,
  computeFinalRanking,
  drawPhase1Matches,
  drawPhaseMatches,
  estimatePacing,
  estimateSchedule,
  fillPlaceholderInTournament,
  initStandings,
  makeGroups,
  rankGroups,
  replacePlayerInTournament,
  resetStandingsForPhase,
} from './padel24';
import { PLAYER_DB, getEffectivePlayerDB, getPlayerTournamentHistory } from './playerDb';
import {
  SERIESPILL_DEFAULT_LOSS_POINTS,
  SERIESPILL_DEFAULT_MATCH_FORMAT,
  SERIESPILL_DEFAULT_MINUTES,
  SERIESPILL_DEFAULT_PROMOTE,
  SERIESPILL_DEFAULT_RELEGATE,
  SERIESPILL_DEFAULT_WIN_POINTS,
  buildSeriespillDivisionMatches,
  calcSeriespillStandings,
  computeSeriespillPromotion,
  createSeriespillTournament,
  endSeriespillRound,
  getClassSetting,
  lockSeasonIfDone,
  proposeSeasonTransition,
  regenerateEmptyDivisionMatches,
  repairSeriespillPairIds,
  replacePlayerInSeries,
  seriespillActiveRound,
  seriespillDefaultUpDown,
  seriespillEffectiveConfig,
  seriespillEffectiveRules,
  seriespillOverdueRounds,
  seriespillRoundDeadline,
  seriespillRoundRevealDate,
  seriespillRoundRevealed,
  seriespillStandingsByDivision,
  shouldLockSeason,
  startNextSeason,
} from './seriespill';
import {
  applySingleMatchElo,
  calcSingleMatchEloChange,
  calcSingleMatchWinner,
  createSingleMatch,
} from './singleMatch';

const PlatformEngine: any = {
  // Version
  version: ENGINE_VERSION,
  versionDate: ENGINE_VERSION_DATE,
  versionName: ENGINE_VERSION_NAME,

  // Change history (newest first)
  changelog: ENGINE_CHANGELOG,

  // ELO system
  elo: {
    /** Live K-factor — reads from EngineSettings (can be changed via the UI) */
    get K_FACTOR() {
      return EngineSettings.K_FACTOR;
    },

    /** ELO levels used in the UI (Nybegynner / Mosjonist / Konkurranse / Elite) */
    LEVELS: ELO_LEVELS,

    /** Find level based on the ELO number */
    getLevel: getEloLevel,

    /** Margin-of-Victory multiplier (1.0–1.5) based on the score margin */
    movMultiplier: calcMovMultiplier,

    /** Compute the ELO change for one team in a match */
    calculateChange: calcEloChange,

    /** Apply the ELO update to all 4 players in the match */
    applyUpdate: applyEloUpdate,

    /** Find the K-multiplier for one player (1.0 established, 2.0 newbie/inactive) */
    getPlayerKMultiplier: getPlayerKMultiplier,

    /** Get the status object for a player (isNew, isReturning, isBoosted) */
    getPlayerStatus: getPlayerStatus,

    /** Format the boost value as compact text: "2x", "1.5x" (saves space in the UI) */
    formatBoost: formatBoost,

    /** Live newbie constants — read from EngineSettings */
    get NEWBIE_MATCH_THRESHOLD() {
      return EngineSettings.NEWBIE_MATCH_THRESHOLD;
    },
    get NEWBIE_GRACE_MATCHES() {
      return EngineSettings.NEWBIE_GRACE_MATCHES;
    },
    get NEWBIE_INACTIVE_DAYS() {
      return EngineSettings.NEWBIE_INACTIVE_DAYS;
    },
    get NEWBIE_K_MULTIPLIER() {
      return EngineSettings.NEWBIE_K_MULTIPLIER;
    },
  },

  /** Engine settings — changed via the UI in the engine view, stored in Storage */
  settings: EngineSettings,

  // Single matches (outside tournaments)
  singleMatch: {
    /** Create a single-match record ready for storage */
    create: createSingleMatch,

    /** Find the winner based on the set results (best of 3) */
    calcWinner: calcSingleMatchWinner,

    /** Compute the ELO change for a single match */
    calcEloChange: calcSingleMatchEloChange,

    /** Apply a single match's ELO change to the player list */
    applyElo: applySingleMatchElo,
  },

  // Drawing of matches
  drawing: {
    /** Weights used in the drawing algorithm */
    weights: {
      PARTNER_PENALTY: 1000,
      OPPONENT_PENALTY: 500,
      HOME_AWAY_PENALTY: 50,
      ELO_PENALTY: 1,
      ELO_THRESHOLD: 100,
      SPEIL_BONUS: -2500,
    },

    /** Draw phase 1 (cross-pairing between A-B, C-D, E-?) */
    drawPhase1: drawPhase1Matches,

    /** Draw phase 2+ (within groups) */
    drawPhase: drawPhaseMatches,
  },

  // Schedule
  schedule: {
    /** Estimate how many phases/rounds fit within the given days */
    estimate: estimateSchedule,

    /**
     * Estimate the waiting time and total day length for each pacing mode,
     * based on the setup (players, courts, phases).
     * Returns { numGroups, matchesPerPhase, modes: { even, back_to_back, one_gap } }
     */
    estimatePacing: estimatePacing,

    /** Build the full match plan with times, courts and placeholders */
    buildFull: buildFullSchedule,

    /**
     * Pacing modes — control how R1 and R2 of the same phase are distributed:
     * - 'even': R1 of all groups first, then R2. Even rhythm, but 175 min between phases.
     * - 'back_to_back': R1 and R2 of the same players right after each other. Shortest time at the venue.
     * - 'one_gap': R1 and R2 with one match in between. A good middle variant.
     */
    PACING_MODES: [
      {
        id: 'even',
        name: 'Likt fordelt',
        description: 'Jevne kamp-mellomrom (105 min snitt). 6t 20m på anlegget.',
      },
      {
        id: 'back_to_back',
        name: 'Back-to-back',
        description: 'R1 og R2 rett etter hverandre (35 min). 4t 45m på anlegget.',
      },
      {
        id: 'one_gap',
        name: 'Én kamp gap',
        description: 'Vent én kamp mellom R1 og R2 (70 min). 5t 33m på anlegget.',
      },
    ],
  },

  // Groups
  groups: {
    /** Split N players into groups based on ELO (ELO-balanced) */
    create: makeGroups,

    /** Rank players within each group based on the day's results */
    rank: rankGroups,

    /** Apply promotion/relegation based on the phase's ranking */
    applyPromotionDemotion: applyPromotionDemotion,
    applyBlockPromotionDemotion: applyBlockPromotionDemotion,

    /** Award bonus points (+3/+2/+1) to the top 3 in each group */
    awardPhaseBonuses: awardPhaseBonuses,
  },

  // Standings (points/results)
  standings: {
    /** Init empty standings for all players */
    init: initStandings,

    /** Apply one match result */
    applyResult: applyMatchResult,

    /** Reset for a new phase (but keep phaseBonuses) */
    resetForPhase: resetStandingsForPhase,

    /** Compute the final ranking based on finals + bonus points */
    computeFinalRanking: computeFinalRanking,
  },

  // Replacing a player mid-tournament (injury/withdrawal)
  replacement: {
    /** Swap out one player for another — only future matches are affected */
    replacePlayer: replacePlayerInTournament,
    fillPlaceholder: fillPlaceholderInTournament,
  },

  // Player database
  playerDB: {
    /** Get the original PLAYER_DB (not modified by tournaments) */
    base: () => PLAYER_DB,

    /** Compute the effective DB with ELO updated from the archive + custom players */
    getEffective: getEffectivePlayerDB,

    /** Get a player's history across tournaments */
    getHistory: getPlayerTournamentHistory,
  },

  // Tournament types
  tournamentTypes: TOURNAMENT_TYPES,
  getTournamentType,
};

// Backward-compatible alias — points at the SAME object as PlatformEngine.
// All existing code uses Padel24Engine.* and therefore works unchanged.
// On a future rename you can use PlatformEngine internally and remove this
// alias once no call sites reference it any longer.
const Padel24Engine: any = PlatformEngine;

// ============================================================
// AMERICANO ENGINE — pure cluster moved to ../src/engine/americano
// (imported at the top of this file). Moved names: AMERICANO_PARTNER_COST,
// AMERICANO_OPPONENT_COST, AMERICANO_IMBALANCE_COST, AMERICANO_SEARCH_ITERATIONS,
// buildAmericanoHistory, calcRoundCost, shuffle, isTennisSetComplete,
// randomRoundPlan, generateAmericanoRoundPlan, selectBenched, teamKey,
// buildTeamHistory, randomTeamRoundPlan, calcTeamRoundCost,
// generateTeamAmericanoRoundPlan, selectBenchedTeams, AMERICANO_DEFAULT_CONFIG,
// createAmericanoTournament, generateAmericanoRound, generateTeamAmericanoRound,
// calcAmericanoStandings, generateAmericanoFinals, generateTeamAmericanoFinals,
// fillPlaceholderInAmericano, replacePlayerInAmericano. The americanoEngine
// object + Padel24Engine.americano wiring stay below.
// ============================================================

// Export the americano engine
const americanoEngine = {
  DEFAULT_CONFIG: AMERICANO_DEFAULT_CONFIG,
  create: createAmericanoTournament,
  generateRound: generateAmericanoRound,
  generateFinals: generateAmericanoFinals,
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

// Attach americano to Padel24Engine (added after the main engine is defined)
if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.americano = americanoEngine;
}

// MEXICANO ENGINE — pure cluster moved to ../src/engine/americano (imported at
// top). Moved names: MEXICANO_POOL_SIZES, defaultMexicanoPoolSize,
// validMexicanoPoolSizes, mexicanoCombinations, scoreMexicanoCombo,
// pickMexicanoFour, pickMexicanoPairing, generatePool6Plan, generatePool8Plan,
// generatePool4Plan, isMexicanoRandomRound, sortPlayersByStandings,
// generateMexicanoRoundPlan, generateMexicanoRound, MEXICANO_DEFAULT_CONFIG,
// createMexicanoTournament. The mexicanoEngine object + wiring stay below.

// Export the mexicano engine
const mexicanoEngine = {
  DEFAULT_CONFIG: MEXICANO_DEFAULT_CONFIG,
  POOL_SIZES: MEXICANO_POOL_SIZES,
  defaultPoolSize: defaultMexicanoPoolSize,
  validPoolSizes: validMexicanoPoolSizes,
  create: createMexicanoTournament,
  generateRound: generateMexicanoRound,
  // These are reused from Americano (same data structure):
  generateFinals: generateAmericanoFinals,
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.mexicano = mexicanoEngine;
}

// MIXICANO ENGINE — pure cluster moved to ../src/engine/americano (imported at
// top). Moved names: effectiveGender, pairCombinations, scoreMixFour, pickMixFour,
// pickMixPairing, generateMixPool4Plan, generateMixPool6Plan, generateMixPool8Plan,
// generateMixicanoRound, MIXICANO_DEFAULT_CONFIG, createMixicanoTournament.
// The mixicanoEngine object + wiring stay below.

// Export the mixicano engine
const mixicanoEngine = {
  DEFAULT_CONFIG: MIXICANO_DEFAULT_CONFIG,
  POOL_SIZES: MEXICANO_POOL_SIZES,
  defaultPoolSize: defaultMexicanoPoolSize,
  validPoolSizes: validMexicanoPoolSizes,
  create: createMixicanoTournament,
  generateRound: generateMixicanoRound,
  effectiveGender,
  // Reused from Americano:
  generateFinals: generateAmericanoFinals,
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.mixicano = mixicanoEngine;
}

// TEAM MEXICANO ENGINE — pure cluster moved to ../src/engine/americano (imported
// at top). Moved names: calcTeamMexicanoStandings, pickTeamPair, pickTeamFourBlock,
// generateTeamMexicanoPool4Plan, generateTeamMexicanoPool6Plan,
// generateTeamMexicanoPool8Plan, generateTeamMexicanoRound,
// TEAM_MEXICANO_DEFAULT_CONFIG, createTeamMexicanoTournament. The
// teamMexicanoEngine object + wiring stay below.

// Export the team_mexicano engine
const teamMexicanoEngine = {
  DEFAULT_CONFIG: TEAM_MEXICANO_DEFAULT_CONFIG,
  POOL_SIZES: MEXICANO_POOL_SIZES,
  create: createTeamMexicanoTournament,
  generateRound: generateTeamMexicanoRound,
  calcTeamStandings: calcTeamMexicanoStandings,
  // Reused from Americano/Mexicano:
  generateFinals: generateAmericanoFinals,
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.team_mexicano = teamMexicanoEngine;
}

// MIX AMERICANO ENGINE — pure cluster moved to ../src/engine/americano (imported
// at top). Moved names: partitionMixAmericanoQuartets, generateMixAmericanoRound,
// MIX_AMERICANO_DEFAULT_CONFIG, createMixAmericanoTournament. The
// mixAmericanoEngine object + wiring stay below.

// Export the mix_americano engine
const mixAmericanoEngine = {
  DEFAULT_CONFIG: MIX_AMERICANO_DEFAULT_CONFIG,
  create: createMixAmericanoTournament,
  generateRound: generateMixAmericanoRound,
  effectiveGender,
  // Reused from Americano:
  generateFinals: generateAmericanoFinals,
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.mix_americano = mixAmericanoEngine;
}

// CLUB AMERICANO ENGINE — pure cluster moved to ../src/engine/americano (imported
// at top). Moved names: buildClubPairs, generateClubAmericanoRound,
// calcClubStandings, generateClubAmericanoFinals, CLUB_AMERICANO_DEFAULT_CONFIG,
// createClubAmericanoTournament. The clubAmericanoEngine object + wiring stay below.

// Export the club_americano engine
const clubAmericanoEngine = {
  DEFAULT_CONFIG: CLUB_AMERICANO_DEFAULT_CONFIG,
  create: createClubAmericanoTournament,
  generateRound: generateClubAmericanoRound,
  calcClubStandings,
  // Dedicated finals function that pairs club ranks against each other
  generateFinals: generateClubAmericanoFinals,
  // Reused from Americano:
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.club_americano = clubAmericanoEngine;
}

// CLUB TEAM AMERICANO ENGINE — pure cluster moved to ../src/engine/americano
// (imported at top). Moved names: buildClubTeamHistory,
// generateClubTeamAmericanoRound, generateClubTeamAmericanoFinals,
// CLUB_TEAM_AMERICANO_DEFAULT_CONFIG, createClubTeamAmericanoTournament. The
// clubTeamAmericanoEngine object + wiring stay below.

const clubTeamAmericanoEngine = {
  DEFAULT_CONFIG: CLUB_TEAM_AMERICANO_DEFAULT_CONFIG,
  create: createClubTeamAmericanoTournament,
  generateRound: generateClubTeamAmericanoRound,
  generateFinals: generateClubTeamAmericanoFinals,
  calcClubStandings, // reused from Club Americano
  // Reused from Americano:
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.club_team_americano = clubTeamAmericanoEngine;
}

// CLUB MEXICANO ENGINE — pure cluster moved to ../src/engine/americano (imported
// at top). Moved names: buildClubMexicanoPairs, generateClubMexicanoRound,
// generateClubMexicanoFinals, CLUB_MEXICANO_DEFAULT_CONFIG,
// createClubMexicanoTournament. The clubMexicanoEngine object + wiring stay below.

const clubMexicanoEngine = {
  DEFAULT_CONFIG: CLUB_MEXICANO_DEFAULT_CONFIG,
  create: createClubMexicanoTournament,
  generateRound: generateClubMexicanoRound,
  generateFinals: generateClubMexicanoFinals,
  calcClubStandings,
  calcStandings: calcAmericanoStandings,
  buildHistory: buildAmericanoHistory,
  fillPlaceholder: fillPlaceholderInAmericano,
  replacePlayer: replacePlayerInAmericano,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.club_mexicano = clubMexicanoEngine;
}

// ============================================================
// KLUBBMESTERSKAP ENGINE
// ============================================================
// Classic padel club championship: fixed pairs, group play (round-robin) followed
// by a knockout stage (single elimination cup). Supports several classes (Herrer/Damer/Mix)
// with multiple divisions (A/B/C ...). All classes share the same court pool and calendar.
//
// Draws follow established padel/tennis practice:
//   - Snake (serpentine) seeding for group play — distributes strength evenly
//   - Cross-over A1-B2/C1-D2/B1-A2/D1-C2 from groups to the quarterfinals
//   - FIP-style bracket seeding for a pure cup (Seed 1 and 2 in opposite halves)
//   - B-knockout (consolation) is optional — off by default
//
// Verified in a Node prototype on 18 May 2026.

// KLUBBMESTERSKAP (KM) ENGINE — front-half pure cluster moved to
// ../src/engine/km and imported at the top of this file. Moved names:
//   KM_DEFAULT_GROUP_MATCH_FORMAT, KM_DEFAULT_GROUP_MINUTES,
//   KM_DEFAULT_KNOCKOUT_MINUTES, KM_DEFAULT_BUFFER_MINUTES, KM_MATCH_FORMATS,
//   KM_CLASS_TEMPLATES, kmFormatSpec, kmSetTarget, kmSetComplete,
//   kmEvaluateSets, determineGroupSizes, determineBracketStructure,
//   snakeSeedGroups, seedGroupsWithRule, sequentialSeedGroups,
//   roundRobinSchedule, buildCrossOverBracket, buildBracketWithLuckyLosers,
//   buildBracketFromAdvancers, fipBracketSeed, calcGroupStandings,
//   calculateKMMatchCount, calculateKMTimeBudget, estimateKMPairCapacity,
//   simulateKMCapacity, buildPairsFromPlayers, sortPairsBySeed,
//   buildDivisionGroupMatches, buildDivisionKnockoutMatches,
//   buildSubsequentARounds, buildBBracketFromGroups, buildConsolationMatches,
//   createKlubbmesterskapTournament, formatMinutesAsTime, scheduleKMMatches,
//   applyCourtPriority, calcKlubbmesterskapBudget.
// The klubbmesterskapEngine object + Padel24Engine.klubbmesterskap wiring stay
// below (they only reference the imported names). The back-half KM result/ELO
// functions (applyKMResult, computeKMClassStandings, recalcKMElo,
// propagateKMResults) remain interleaved among the React components.

const klubbmesterskapEngine = {
  // Constants
  DEFAULT_GROUP_MATCH_FORMAT: KM_DEFAULT_GROUP_MATCH_FORMAT,
  DEFAULT_GROUP_MINUTES: KM_DEFAULT_GROUP_MINUTES,
  DEFAULT_KNOCKOUT_MINUTES: KM_DEFAULT_KNOCKOUT_MINUTES,
  DEFAULT_BUFFER_MINUTES: KM_DEFAULT_BUFFER_MINUTES,
  MATCH_FORMATS: KM_MATCH_FORMATS,
  CLASS_TEMPLATES: KM_CLASS_TEMPLATES,

  // Draw and bracket
  snakeSeedGroups,
  roundRobinSchedule,
  buildCrossOverBracket,
  buildBracketWithLuckyLosers,
  fipBracketSeed,
  determineGroupSizes,
  determineBracketStructure,

  // Building and standings
  buildPairsFromPlayers,
  sortPairsBySeed,
  calcGroupStandings,

  // Main factory
  create: createKlubbmesterskapTournament,
  scheduleMatches: scheduleKMMatches,
  applyCourtPriority: applyCourtPriority,

  // Budgeting
  calcMatchCount: calculateKMMatchCount,
  calcTimeBudget: calculateKMTimeBudget,
  calcBudget: calcKlubbmesterskapBudget,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.klubbmesterskap = klubbmesterskapEngine;
}

// ============================================================
// SERIESPILL (LEAGUE-PLAY) ENGINE — config/defaults, division match builder,
// standings, promotion/relegation, the tournament factory, the season
// life-cycle (lock/reveal/transition/startNextSeason), data repair, the slot
// engine and the match-court/date/time accessors moved to
// ../src/engine/seriespill and imported above. The seriespillEngine wiring
// object + Padel24Engine.seriespill assignment stay below (mirrors the
// booking/KM precedent). applySerieResult stays interleaved among the React
// components (it calls recalcKMElo, a back-half KM function not yet moved).
// ============================================================

const seriespillEngine = {
  // Constants
  DEFAULT_MATCH_FORMAT: SERIESPILL_DEFAULT_MATCH_FORMAT,
  DEFAULT_MINUTES: SERIESPILL_DEFAULT_MINUTES,
  DEFAULT_WIN_POINTS: SERIESPILL_DEFAULT_WIN_POINTS,
  DEFAULT_LOSS_POINTS: SERIESPILL_DEFAULT_LOSS_POINTS,
  DEFAULT_PROMOTE: SERIESPILL_DEFAULT_PROMOTE,
  DEFAULT_RELEGATE: SERIESPILL_DEFAULT_RELEGATE,
  MATCH_FORMATS: KM_MATCH_FORMATS, // reuse KM's match formats

  // Building and logic
  buildDivisionMatches: buildSeriespillDivisionMatches,
  calcStandings: calcSeriespillStandings,
  computePromotion: computeSeriespillPromotion,
  defaultUpDown: seriespillDefaultUpDown,
  roundDeadline: seriespillRoundDeadline,
  activeRound: seriespillActiveRound,
  effectiveRules: seriespillEffectiveRules,
  replacePlayer: replacePlayerInSeries,
  // Season life-cycle
  shouldLockSeason,
  lockSeasonIfDone,
  standingsByDivision: seriespillStandingsByDivision,
  proposeSeasonTransition,
  startNextSeason,
  repairPairIds: repairSeriespillPairIds,
  getClassSetting,
  effectiveConfig: seriespillEffectiveConfig,
  regenerateEmptyDivisionMatches,
  endRound: endSeriespillRound,
  overdueRounds: seriespillOverdueRounds,
  roundRevealed: seriespillRoundRevealed,
  roundRevealDate: seriespillRoundRevealDate,

  // Main factory
  create: createSeriespillTournament,

  // Court prioritization reused from KM (only relevant when courtMode is 'allocated')
  applyCourtPriority: applyCourtPriority,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.seriespill = seriespillEngine;
}

// ============================================================
// BOOKING ENGINE
//
// Module for booking courts at the center. Standalone for now — the tournament
// modules don't use it yet, but it is planned to be integrated in stage 3.
//
// Data model (one booking):
//   {
//     id: 'book_<timestamp>_<rand>',
//     court: 1,                          // court number (1-indexed)
//     date: '2026-06-15',                // ISO date
//     startTime: '12:00',                // HH:MM
//     endTime: '13:30',                  // HH:MM (exclusive)
//     durationMin: 90,
//     bookedBy: { name, phone, email },  // free text — no user accounts yet
//     paymentStatus: 'pending'|'paid'|'free',
//     priceNok: 0,                       // set in stage 2
//     createdAt: <ms>,
//     notes: '',
//   }
//
// Central rule ("air between bookings"): Two bookings on the SAME court must have at
// least `bufferMin` between them. This avoids a court being booked 11-12 and 12:30-13:30 —
// a 30 min gap is typically too short to be useful. bufferMin is configurable
// (default 30) and can be overridden per club in stage 2.
//
// Cross-court rule: If court 1 is free from 12:00 but court 2 is free from 13:00,
// court 1 cannot be booked at 13:00 (that creates a gap 12:00-13:00 on court 1 that is
// too small to be useful). Implemented as a validation that a booking does not
// start > bufferMin after the court's previous end time if there are any earlier
// bookings the same day. (We do this in stage 2 — in stage 1 we only have the
// simple same-court buffer.)
// ============================================================
// BOOKING ENGINE config (BOOKING_DEFAULT_BUFFER_MIN / BOOKING_DEFAULT_DURATION_MIN
// / BOOKING_DEFAULT_CONFIG) moved to ../src/engine/booking and imported above.

// Get the effective facilities for a court: per-court override over the globals.
// courtFacilities ->src/engine/booking (PR-K)

// Get the display name for a court: nickname if set, otherwise "Bane N".
// courtDisplayName ->src/engine/booking (PR-K)

// ============================================================
// BANEPAD: pure helpers (derive the court's timeline + what is happening now)
// ============================================================
// Used by BanepadCentralView (main screen) and BanepadCourtScreen (pad at the
// court). No storage, no React — derives only from bookings + the active
// tournament/league. Local date format (NEVER toISOString) per the project rules.

// banepad pure helpers (cluster 1: banepadCourtList, banepadMatchStartMin,
// banepadTimelineForCourt, banepadFindCourtMatch, banepadResolveCourt) moved to
// ../src/engine/banepad (imported at top). banepadTournamentView stays below
// (frontend view-routing concern).

// Which app view an active tournament opens in (same mapping as the front page).
function banepadTournamentView(type: any): string {
  switch (type) {
    case 'americano':
      return 'americano';
    case 'mexicano':
      return 'mexicano';
    case 'mixicano':
      return 'mixicano';
    case 'team_mexicano':
      return 'teamMexicano';
    case 'mix_americano':
      return 'mixAmericano';
    case 'club_americano':
      return 'clubAmericano';
    case 'club_team_americano':
      return 'clubTeamAmericano';
    case 'club_mexicano':
      return 'clubMexicano';
    case 'klubbmesterskap':
      return 'klubbmesterskap';
    default:
      return 'tournament';
  }
}

// Recurrence expansion (expandRecurrence) moved to ../src/engine/booking and
// imported above (shared by the booking calendar and the seriespill slot
// engine).

// Time helpers toMinHHMM/fromMinHHMM moved to ../src/lib/date and imported above.

// Check whether a candidate booking collides with existing bookings on the same court.
// Returns { ok: bool, reason?: string, conflictId?: string }.
// ── Seriespill slot engine moved to ../src/engine/seriespill ──────────────
// serieGenerateSlots, serieBuildReservations and serieAssignMatchesToSlots are
// imported above and used directly by the React components. serieExpandWeekdayDates
// (unused) was moved along with the cluster for cohesion.

// BOOKING ENGINE moved to ../src/engine/booking and imported above:
//   gapFillableByDurations, validateBooking, createBooking, getBookingsForDay,
//   DAY_OF_WEEK_KEYS, DAY_OF_WEEK_LABELS, HOLIDAYS,
//   NORWEGIAN_HOLIDAYS_FIXED, HOLIDAY_NAMES_FIXED, HOLIDAY_NAMES_MOVABLE,
//   holidayName, getHolidaySet, isHoliday, isNorwegianHoliday, dayOfWeekKey,
//   zoneMatches, findZoneAt, calcBookingPrice, findFreeSlots.
// The bookingEngine object + Padel24Engine.booking wiring stay below (they
// also reference courtDisplayName/courtFacilities, which have not moved yet;
// expandRecurrence now comes from ../src/engine/booking, imported above).

const bookingEngine = {
  DEFAULT_BUFFER_MIN: BOOKING_DEFAULT_BUFFER_MIN,
  DEFAULT_DURATION_MIN: BOOKING_DEFAULT_DURATION_MIN,
  DEFAULT_CONFIG: BOOKING_DEFAULT_CONFIG,
  // No DEFAULT_ZONES (#1312): a club with no saved zone has no court price.
  DAY_OF_WEEK_KEYS,
  DAY_OF_WEEK_LABELS,
  validateBooking,
  createBooking,
  getBookingsForDay,
  isNorwegianHoliday,
  isHoliday,
  getHolidaySet,
  holidayName,
  HOLIDAYS,
  dayOfWeekKey,
  zoneMatches,
  findZoneAt,
  calcBookingPrice,
  courtPricesSet,
  calcCourtBookingPrice,
  findFreeSlots,
  courtDisplayName,
  courtFacilities,
  expandRecurrence,
  toMin: toMinHHMM,
  fromMin: fromMinHHMM,
};

if (typeof Padel24Engine !== 'undefined') {
  Padel24Engine.booking = bookingEngine;
}

export { PlatformEngine, Padel24Engine, banepadTournamentView };
